import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { useAuthContext } from 'contexts/AuthContext';
import PageTitle from 'components/PageTitle/PageTitle';
import RenderIf from 'components/RenderIf/RenderIf';
import EmptyState from 'components/EmptyState/EmptyState';
import useGetDealsWithProFormas from 'views/ProFormaTable/hooks/useGetDealsWithProFormas';
import ProFormaTableTabs from 'views/ProFormaHome/components/ProFormaTableTabs/ProFormaTableTabs';
import CreateProFormaModal from 'views/CreateProFormaModal/CreateProFormaModal';
import { URLS } from 'services/urls';
import { NSButton, NSModal, NSSelect } from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import { DISABLED_PRO_FORMA_BTN_TOOLTIP } from 'views/Deals/constants';

const ProFormas = () => {
    const navigate = useNavigate();

    const { selectedOrganizationId } = useAuthContext();
    const [showDealSelectionModal, setShowDealSelectionModal] = useState(false);
    const [selectedDealId, setSelectedDealId] = useState<number | null>(null);
    const [openCreateProFormaModal, setOpenCreateProFormaModal] = useState(false);
    const [selectedDealOption, setSelectedDealOption] = useState<IOptionAnyValue | null>(null);

    const { data: dealsWithProFormas, isLoading } = useGetDealsWithProFormas({
        orgId: selectedOrganizationId!,
        enabled: !!selectedOrganizationId,
        includeAllDealsWithProFormas: true,
    });

    const hasAnyProFormas = dealsWithProFormas?.some(
        deal => deal.proFormas.length > 0 || deal.uploadedProFormas.length > 0,
    );

    const handleDealNameClick = (dealId: number) => {
        const path = generatePath(URLS.DEAL_DASHBOARD.HOME, { dealId });
        window.open(path, '_blank');
    };

    const handleCreateClick = () => {
        if (dealsWithProFormas && dealsWithProFormas.length === 1) {
            setSelectedDealId(dealsWithProFormas[0].id);
            setOpenCreateProFormaModal(true);
        } else {
            setShowDealSelectionModal(true);
        }
    };

    const handleDealSelection = () => {
        if (!selectedDealOption) return;

        const dealId = selectedDealOption.value as number;
        setShowDealSelectionModal(false);
        setSelectedDealId(dealId);
        setSelectedDealOption(null);
        setOpenCreateProFormaModal(true);
    };

    const handleModalClose = () => {
        setShowDealSelectionModal(false);
        setSelectedDealOption(null);
    };

    const toggleCreateProFormaModal = () => {
        setOpenCreateProFormaModal(!openCreateProFormaModal);
        if (openCreateProFormaModal) setSelectedDealId(null);
    };

    const handleCreateDeal = () => {
        navigate(generatePath(URLS.DEALS.FORM, { dealId: 'new' }));
    };

    return (
        <>
            <PageTitle
                title="Pro Formas"
                displayBreadcrumb
                pageMetaDataTitle="Pro Formas"
                containerClassName="mb-3"
                beta
                betaCustomText="Beta"
            >
                <NSButton
                    color="primary"
                    outline={false}
                    id="create-pro-forma-btn"
                    text="Create pro forma"
                    callback={handleCreateClick}
                    className="heap-pro-forma-pipeline-pro-forma-create-pro-forma"
                    disabled={!dealsWithProFormas || dealsWithProFormas.length === 0}
                    disabledTooltip={DISABLED_PRO_FORMA_BTN_TOOLTIP}
                />
            </PageTitle>

            <RenderIf isTrue={!hasAnyProFormas && !isLoading}>
                <EmptyState
                    title="No pro formas in this organization yet!"
                    subtitle="Create your deal first, then generate a pro forma."
                    className="mt-2 vh-100"
                >
                    <NSButton text="Create deal" color="primary" outline={false} className="NSButton" callback={handleCreateDeal} />
                </EmptyState>
            </RenderIf>

            <RenderIf isTrue={hasAnyProFormas || isLoading} isLoading={isLoading}>
                {dealsWithProFormas?.map(deal => (
                    <div key={deal.id} className="mb-4">
                        <div className="d-flex align-items-center mb-2 text-dark">
                            <h3 className="mb-0 text-dark">{deal.dealName}</h3>
                            <FontAwesomeIcon
                                icon={faExternalLink}
                                className="ml-2 cursor--pointer pt-1"
                                onClick={() => handleDealNameClick(deal.id)}
                                role="button"
                                aria-label={`Open ${deal.dealName} in new tab`}
                            />
                        </div>
                        <RenderIf isTrue={deal.description}>
                            <h4 className="mb-2">{deal.description}</h4>
                        </RenderIf>
                        <ProFormaTableTabs
                            proFormas={deal.proFormas}
                            uploadedProFormas={deal.uploadedProFormas}
                            dealId={deal.id}
                            isAllDealsProFormasPage
                        />
                    </div>
                ))}
            </RenderIf>

            <NSModal
                isOpen={showDealSelectionModal}
                toggle={handleModalClose}
                modalHeaderText="Select a deal to create pro forma for"
                modalHeaderClassName="text-white bg-primary"
                modalFooterButtonText="Continue"
                modalFooterButtonColor="primary"
                modalFooterCancelButtonText="Cancel"
                modalFooterButtonFunction={handleDealSelection}
                modalFooterIsButtonFunctionDisabled={!selectedDealOption}
            >
                <NSSelect
                    name="deal-selection"
                    label="Select Deal"
                    placeholder="Search for a deal..."
                    options={dealsWithProFormas?.map(deal => ({
                        label: deal.dealName,
                        value: deal.id,
                    })) || []}
                    value={selectedDealOption}
                    onChange={(option: IOptionAnyValue) => setSelectedDealOption(option)}
                    isClearable
                />
            </NSModal>

            <RenderIf isTrue={openCreateProFormaModal && selectedDealId !== null}>
                <CreateProFormaModal
                    toggle={toggleCreateProFormaModal}
                    isOpen={openCreateProFormaModal}
                    dealId={selectedDealId || undefined}
                    dealAssetTypeCorrelationId={
                        dealsWithProFormas?.find(d => d.id === selectedDealId)?.assetTypeCorrelationId || undefined
                    }
                />
            </RenderIf>
        </>
    );
};

export default ProFormas;
